<!--
  A view that lays out the about form
-->
<template>
  <div class="ms-address">

    <div v-if="showPostcodeLookup && !manualAddress">
    <!-- Postcode lookup -->
    <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.address`) }}</label>
        <MsPostcodeInput
        v-model="searchedPostcode"
        @onSubmitted="onPostcodeLookupSubmitted"
        @onSuccess="onPostcodeLookupSuccess"
        @onError="onPostcodeLookupError"
        :class="{'is-danger': postcodeLookupError.status}"
        />
        <span
        v-show="postcodeLookupError.status"
        class="help is-danger"
        >
        {{ $t(postcodeLookupError.message) }}
        </span>
    </div>


    <!-- Address select -->
    <div v-if="addressOptionsArray.length > 0">
        <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.selectAddressPlaceholderText`) }}</label>
        <div class="control">
            <appSelect
            v-model="selectedAddress"
            class="is-fullwidth"
            :options="addressOptionsArray"
            :isLoading="fetchingAddress"
            :placeholder="$t(`${pagei18nKey}.selectAddressPlaceholderText`)"
            display-key="text"
            @change="onAddressSelected"
            />
        </div>
        </div>
    </div>

    <div
        v-if="!manualAddress"
        class="ms-address__address-selection-option">
        <a
        href="#"
        @click.prevent="manualAddress = true; onFormUpated()">
        <span v-if="selectedAddress === null" class="link-text">
            {{ $t(`${pagei18nKey}.manualAddress`) }}
        </span>
        <span v-if="selectedAddress !== null" class="link-text">
            {{ $t(`${pagei18nKey}.editAddress`) }}
        </span>
        </a>
    </div>
    </div>

    <!-- Address -->
    <div v-show="manualAddress || (!showPostcodeLookup && country !== null)">

    <!-- Address 1 -->
    <!--
    todo
        We use v-validate:{computed} to effectively add a watch to the model so when we change it dynamically in
        code the validation is still run. This is a bit aggressive as it will run early, will move to explicitly
        validating in code
    -->
    <div class="field">
        <label class="label">{{ $t(`${pagei18nKey}.address`) }}</label>
        <div class="control has-icons-right">
        <input
            v-model="address1"
            v-validate:address1="'required'"
            :data-vv-as="$t(`${pagei18nKey}.addressLine1`)"
            class="input"
            name="address1"
            type="text"
            :placeholder="$t(`${pagei18nKey}.addressLine1`)"
            :class="{'is-danger': errors.has('address1')}"
            :autocomplete="isKiosk ? 'nope' : 'address-line1'"
        >
        <span v-show="fields.address1 && fields.address1.valid" class="icon is-small is-right">
            <i class="fas fa-check"></i>
        </span>
        </div>

        <span
        v-show="errors.has('address1')"
        class="help is-danger"
        >
        {{ errors.first('address1') }}
        </span>
    </div>

    <!-- Address 2 -->
    <div class="field">
        <div class="control has-icons-right">
        <input
            v-model="address2"
            class="input"
            name="address2"
            type="text"
            :placeholder="$t(`${pagei18nKey}.addressLine2`)"
            :autocomplete="isKiosk ? 'nope' : 'address-line2'"
        >
        <span class="icon is-small is-right">
            <i class="fas fa-check"></i>
        </span>
        </div>
    </div>

    <!-- Address 3 -->
    <div class="field">
        <div class="control has-icons-right">
        <input
            v-model="address3"
            class="input"
            name="address3"
            type="text"
            :placeholder="$t(`${pagei18nKey}.addressLine3`)"
            :autocomplete="isKiosk ? 'nope' : 'address-line3'"
        >
        <span class="icon is-small is-right">
            <i class="fas fa-check"></i>
        </span>
        </div>
    </div>

    <!-- City -->
    <!--
        todo see v-validate above
    -->
    <div class="field">
        <div class="control has-icons-right">
        <input
            v-model="city"
            v-validate:city="'required'"
            :data-vv-as="$t(`${pagei18nKey}.city`)"
            class="input"
            :class="{'is-danger': errors.has('city')}"
            name="city"
            type="text"
            :placeholder="$t(`${pagei18nKey}.city`)"
            :autocomplete="isKiosk ? 'nope' : 'address-level2'"
        >
        <span
            v-show="fields.city && fields.city.valid"
            class="icon is-small is-right"
        >
            <i class="fas fa-check"></i>
        </span>
        </div>
        <span
        v-show="errors.has('city')"
        class="help is-danger"
        >
        {{ errors.first('city') }}
        </span>
    </div>

    <!-- County -->
    <!--
        todo see v-validate above
    -->
    <div class="field">
        <div class="control has-icons-right">
        <input
            v-model="county"
            v-validate:county="'required'"
            :data-vv-as="$t(`${pagei18nKey}.county`)"
            class="input"
            :class="{'is-danger': errors.has('county')}"
            name="county"
            type="text"
            :placeholder="$t(`${pagei18nKey}.county`)"
            :autocomplete="isKiosk ? 'nope' : 'address-level1'"
        >
        <span
            v-show="fields.county && fields.county.valid"
            class="icon is-small is-right"
        >
            <i class="fas fa-check"></i>
        </span>
        </div>
        <span
        v-show="errors.has('county')"
        class="help is-danger"
        >
        {{ errors.first('county') }}
        </span>
    </div>

    <!-- Postcode -->
    <!--
        todo see v-validate above
    -->
    <div class="field">
        <div class="control has-icons-right">
        <input
            v-model="postcode"
            v-validate:postcode="'required'"
            :data-vv-as="$t(`${pagei18nKey}.postcode`)"
            class="input"
            :class="{'is-danger': errors.has('postcode')}"
            name="postcode"
            type="text"
            :placeholder="$t(`${pagei18nKey}.postcode`)"
            :autocomplete="isKiosk ? 'nope' : 'postal-code'"
        >
        <span
            v-show="fields.postcode && fields.postcode.valid"
            class="icon is-small is-right"
        >
            <i class="fas fa-check"></i>
        </span>
        </div>
        <span
        v-show="errors.has('postcode')"
        class="help is-danger"
        >
        {{ errors.first('postcode') }}
        </span>
    </div>

    
    <!-- Name on door buzzer -->
    <div class="field">
        <div class="control has-icons-right">
        <input
            v-model="nameOnDoorBuzzer"
            class="input"
            name="nameOnDoorBuzzer"
            type="text"
            :placeholder="$t(`${pagei18nKey}.nameOnDoorBuzzer`)"
            autocomplete="nope"
        >
        <span class="icon is-small is-right">
            <i class="fas fa-check"></i>
        </span>
        </div>
    </div>

    <!-- Country -->
    <div class="field">
        <div class="control is-expanded has-icons-right">
        <div class="is-fullwidth">
            <AppSelect
            v-model="country"
            v-validate:country="'required'"
            displayKey="name"
            :placeholder="$t(`${pagei18nKey}.country`)"
            class="is-fullwidth"
            :options="countries"
            :class="{'is-danger': errors.has('country')}"
            name="country"/>
        </div>
      </div>
        <p
            v-show="errors.has('country')"
            class="help is-danger">
            {{ errors.first('country') }}
        </p>
    </div>

    <!-- Fiscal code -->
    <div class="field" v-if="showFiscalCode">
        <div class="control has-icons-right">
        <input
            v-model="localFiscalCode"
            :data-vv-as="$t(`${pagei18nKey}.fiscalCode`)"
            class="input"
            :class="{'is-danger': errors.has('localFiscalCode')}"
            name="localFiscalCode"
            type="text"
            @input="onFiscalCodeChanged"
            :placeholder="$t(`${pagei18nKey}.fiscalCode`)"
            autocomplete="nope"
        >
        <span
            v-show="fields.fiscalCode && fields.fiscalCode.valid"
            class="icon is-small is-right"
        >
            <i class="fas fa-check"></i>
        </span>
        </div>
        <span
        v-show="errors.has('localFiscalCode')"
        class="help is-danger"
        >
        {{ errors.first('localFiscalCode') }}
        </span>
    </div>

    <div
        v-if="showPostcodeLookup"
        class="ms-address__address-selection-option"
    >
        <span class="link-text" @click="searchForDifferentAddressClick">
        {{ $t(`${pagei18nKey}.searchDifferentAddress`) }}
        </span>
    </div>
    </div>


  </div>
</template>
<script>
  import api from '@/api/api'
  import MsPostcodeInput from '@/components/ms-postcode-input'
  import PostcodeLookup from '@/cloud/postcode-lookup'
  import AppSelect from '@/components/app-select'
  import appSettings from '@/app-settings.json'
  import {SelectedValue} from '@/model/selectedValue'
  import {Address} from '@/model/address'

  /**
   *  Postcode lookup error messages
   */
  const postcodeLookupErrorMessages = {
    INVALID: 'message.postcodeLookup.errorMessages.invalid',
    NOT_FOUND: 'message.postcodeLookup.errorMessages.notFound'
  }

  export default {

    props: {
      address: {
        type: Object
      },
      fiscalCode: {
        type: String,
        required: false
      },
      askFiscalCode: {
        type: Boolean,
        required: false,
        default: false
      }
    },

    components: {
      MsPostcodeInput,
      AppSelect
    },

    data: function () {
      return {
        countries: [],
        postcodeLookupError: {
          status: false,
          message: ''
        },
        fetchingAddress: false,
        pagei18nKey: 'message.about',
        localAddress: new Address({}),
        localFiscalCode: this.fiscalCode,
        addressOptionsArray: [],
        selectedAddressLocal: null,
        searchedPostcode: '',
        manualAddress: false,
        isKiosk: !!appSettings.kioskSettings
      }
    },

    mounted () {
      this.localAddress.address1 = this.address.address1
      this.localAddress.address2 = this.address.address2
      this.localAddress.address3 = this.address.address3
      this.localAddress.nameOnDoorBuzzer = this.address.nameOnDoorBuzzer
      this.localAddress.city = this.address.city
      this.localAddress.county = this.address.county
      this.localAddress.country = this.address.country
      this.localAddress.postcode = this.address.postcode

      // if we have an address show the expanded address form
      if (this.hasAddress) this.manualAddress = true

      /**
       * Get countries data from api
       */
      api.getCountries()
        .then(data => {
          this.countries = data
          this.onFormUpated()
        })
        .catch(console.error)
    },

    methods: {

      validate: function () {
        this.$validator.validate()
      },
      /**
       * Clears all address data
       */
      clearAddress: function () {
        this.address1 = null
        this.address2 = null
        this.address3 = null
        this.city = null
        this.county = null
        this.postcode = null
        this.country = null
        this.nameOnDoorBuzzer = null
      },

      /**
       * Called when postcode lookup is submitted.
       * Clears the selected address and the addres options array
       */
      onPostcodeLookupSubmitted: function () {
        this.selectedAddress = null
        this.addressOptionsArray = []
        this.clearAddress()
      },

      /**
       * Called when postcode lookup is successful.
       * Clears errors, hide manual address and sets addressOptiosnArray with
       * postcode lookup results
       *
       * @params {PostcodeResult[]} postcodeResults - The results the postcode lookup
       */
      onPostcodeLookupSuccess: function (postcodeResults) {
        this.postcodeLookupError = false
        this.manualAddress = false
        this.addressOptionsArray = postcodeResults
      },

      /**
       * Called when postcode lookup has errored.
       */
      onPostcodeLookupError: function (error) {
        this.postcodeLookupError = {
          status: true,
          message: postcodeLookupErrorMessages[error]
        }
      },

      /**
       * Called when the address is selected in the address select
       */
      onAddressSelected: function () {
        // If the selectedAddress is null its the placeholder item, clear the
        // address, and dont do anything else
        if (this.selectedAddress === null) {
          this.clearAddress()
          return
        }

        this.fetchingAddress = true
        let address = this.addressOptionsArray[this.selectedAddress]

        // Construct the postcode lookup api
        let postcodeLookup = new PostcodeLookup(
          appSettings.pcaPredictConfig.apiKey
        )

        // Perform the lookup with the selected addresses id
        postcodeLookup.addressLookup(address.id).then((foundAddress) => {
          console.log(foundAddress)
          foundAddress = foundAddress[0]

          // Check for error flag
          if (foundAddress.Error) {
            this.postcodeLookupError = true
            return
          }

          // Set address fields
          this.address1 = foundAddress.address1
          this.address2 = foundAddress.address2
          this.address3 = foundAddress.address3
          // this.nameOnDoorBuzzer = foundAddress.nameOnDoorBuzzer - we don't laod from api
          this.city = foundAddress.city
          this.county = foundAddress.county
          this.postcode = foundAddress.postcode
          this.country = this.countries.find((c) => c.iso2Code === foundAddress.countryIso2)

          // This is set here instead of inside a finally because
          // finally is not supported in IE / Edge / Safari
          // Stop the loading spinner
          this.fetchingAddress = false
          this.manualAddress = true
        })
      },

      /**
       * Get the index of an object that has a matching key
       *
       * @param {string|number} value - Value to match
       * @param {Object[]} data - The data to check in
       * @param {string} key - The key to check value in each object
       */
      getIndexOfObjectFromKey: function (value, data, key) {
        let foundIndex = data.findIndex(item => {
          return item[key] === value
        })
        return foundIndex === -1 ? null : foundIndex
      },

      /**
       * Reset back to an empty postcode lookup field
       */
      searchForDifferentAddressClick: function () {
        this.manualAddress = false
        this.addressOptionsArray = []
        this.selectedAddress = null
        this.clearAddress()
      },

      onFormUpated () {
        // in a settimeout as the form isn't updated until the end of this event cycle
        // that means isValid check is one step behind unless we wrap in timeout.
        setTimeout(() => this.$emit('update:address', {address: this.localAddress, formVisible: this.manualAddress}), 0)
        this.onFiscalCodeChanged()
      },

      onFiscalCodeChanged () {
        // clear if no longer visible
        if (!this.showFiscalCode) {
          this.localFiscalCode = null
        }
        this.$emit('update:fiscalCode', this.localFiscalCode)
      },

      /**
       * Checks each form field to see if the form is valid
       *
       * @returns {boolean} Whether form is valid
       */
      isFormValid () {
        let valid = Object.keys(this.fields).every(key => this.fields[key].valid)
        return valid
      }
    },

    computed: {

      hasAddress: {
        get () {
          // if any part of address has a value then show the address form
          return (this.address1 || this.address2 || this.address3 || this.city || this.county || this.postcode || this.country)
        }
      },
      selectedAddress: {
        /**
         * Gets current stored selected Address
         */
        get () {
          if (this.selectedAddressLocal !== null) {
            return this.selectedAddressLocal.index
          }
          return null
        },

        /**
         * Sets selected Address value
         * @param {number} value - The selected index of the address dropdown
         */
        set (value) {
          this.selectedAddressLocal = value
        }
      },

      address1: {
        /**
         * Gets current stored address line 1
         */
        get () {
          return this.localAddress.address1
        },

        /**
         * Sets address line 1 value
         *
         * @param {string} value - Value to set address line 1 to
         */
        set (value) {
          this.localAddress.address1 = value
          this.onFormUpated()
        }
      },

      address2: {
        /**
         * Gets current stored address line 2
         */
        get () {
          return this.localAddress.address2
        },

        /**
         * Sets address line 2 value
         *
         * @param {string} value - Value to set address line 2 to
         */
        set (value) {
          this.localAddress.address2 = value
          this.onFormUpated()
        }
      },

      address3: {
        /**
         * Gets current stored address line 2
         */
        get () {
          return this.localAddress.address3
        },

        /**
         * Sets address line 2 value
         *
         * @param {string} value - Value to set address line 2 to
         */
        set (value) {
          this.localAddress.address3 = value
          this.onFormUpated()
        }
      },

      nameOnDoorBuzzer: {
        /**
         * Gets current stored name on door buzzer
         */
        get () {
          return this.localAddress.nameOnDoorBuzzer
        },

        /**
         * Sets name on door buzzer value
         *
         * @param {string} value - Value to set name on door buzzer to
         */
        set (value) {
          this.localAddress.nameOnDoorBuzzer = value
          this.onFormUpated()
        }
      },

      city: {
        /**
         * Gets current stored city
         */
        get () {
          return this.localAddress.city
        },

        /**
         * Sets city value
         *
         * @param {string} value - Value to set city to
         */
        set (value) {
          this.localAddress.city = value
          this.onFormUpated()
        }
      },

      county: {
        /**
         * Gets current stored county
         */
        get () {
          return this.localAddress.county
        },

        /**
         * Sets county value
         *
         * @param {string} value - Value to set county to
         */
        set (value) {
          this.localAddress.county = value
          this.onFormUpated()
        }
      },

      postcode: {
        /**
         * Gets current stored postcode
         */
        get () {
          return this.localAddress.postcode
        },

        /**
         * Sets postcode value
         *
         * @param {string} value - Value to set postcode to
         */
        set (value) {
          this.localAddress.postcode = value
          this.onFormUpated()
        }
      },

      country: {
        /**
         *  Gets the index for current stored country
         */
        get () {
          let selectedValue = this.localAddress.country
          let index = null
          if (selectedValue) {
            index = this.getIndexOfObjectFromKey(
              selectedValue.id,
              this.countries,
              'id'
            )
          }
          return index
        },

        /**
         * Sets country value and text
         *
         * @param {Object} value - The object of the selected country
         */
        set (value) {
          if (value != null) {
            this.localAddress.country = new SelectedValue(value.id, value.name)
          } else {
            this.localAddress.country = null
          }
          this.onFormUpated()
        }
      },

      /**
       * Checks whether selected country supports postcode lookup
       */
      showPostcodeLookup: {
        get () {
          return (
            true
            // this.countries[this.country] !== undefined &&
            // this.countries[this.country].postcodeLookupSupported
          )
        }
      },

      /**
      * Checks whether to show the fiscal code field
      */
      showFiscalCode: {
        get () {
          return this.askFiscalCode && (
            this.countries[this.country] !== undefined &&
            this.countries[this.country].showFiscalCode
          )
        }
      }
    }
  }
</script>

<style lang="scss">
  @import "../assets/sass/main.scss";
  .ms-address__address-selection-option {
    margin: $dims-spacing-large 0 $dims-padding-default 0;

    .link-text {
      font-weight: $semibold-font-weight;
      color: $quaternary-text-color;
      cursor: pointer;
    }
  }
</style>
